import { ProgressRegisterDatasetTool, RenderRegisterDatasetTool } from './RegisterDatasetTool.render.tsx';
import { type RegisterDatasetInput, RegisterDatasetInputSchema, type RegisterDatasetOutput } from './schema.ts';
import { registerLiteralStep } from '#plugin-chat/lib/data-pipeline/steps/register-literal.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { createTool } from '#plugin-chat/lib/tool/create-tool.tsx';
import type { ProducerToolConfig } from '#plugin-chat/lib/tool/create-tool.types.ts';

/**
 * Tool for storing datasets for later reference.
 *
 * Provides clean separation of concerns — RegisterDatasetTool stores data,
 * TransformDatasetTool transforms existing data. This makes LLM tool selection clearer.
 *
 * Producer tool: the framework runs `produceDataset`, stages under
 * `args.outputDataset`, then calls `execute` to format the LLM-facing envelope.
 * Post-reload recovery re-runs `produceDataset` via the dataset recovery
 * registry.
 */
export const RegisterDatasetTool = createTool<object, RegisterDatasetInput, RegisterDatasetOutput>(
  'registerDataset',
  () => ({
    instructions: (
      <ToolGuidance
        tool={RegisterDatasetTool}
        whenNotToUse={[
          'Retrieving data from CDF — use queryKnowledgeGraph or getDatapoints',
          'Transforming an already-stored dataset — use transformDataset',
        ]}
        whenToUse={[
          'Injecting static/literal data (e.g. user-provided tables) for downstream tools to consume',
          'Capturing computed data from the conversation as a dataset for chaining into transformDataset or showDashboard',
        ]}
      />
    ),
    config: {
      parameters: RegisterDatasetInputSchema,
      render: RenderRegisterDatasetTool,
      progress: ProgressRegisterDatasetTool,
      description: <p>Store a flat-record dataset under an outputDataset for later reference.</p>,
      produceDataset: (args, ctx) =>
        registerLiteralStep.execute({ rows: args.data, title: args.title }, undefined, ctx),
      execute: ({ args, dataset }) => {
        const titleSuffix = args.title ? ` (${args.title})` : '';
        return {
          success: true as const,
          outputDataset: args.outputDataset,
          message: `Stored ${dataset.rows?.length} records as "${args.outputDataset}"${titleSuffix}`,
        };
      },
    } satisfies ProducerToolConfig<RegisterDatasetInput, RegisterDatasetOutput>,
  }),
);
